import React, { useState, useEffect, useRef } from "react";
import { Card, Button, ProgressBar, Form, Container, Row, Col, Toast, ToastContainer, Spinner } from "react-bootstrap";
import { BrowserMultiFormatReader } from "@zxing/library";

import { getAssetInfoApi, getValidateScanDataApi } from "../asset-list/asset-api";
import "./checking.css";
import close from "../../assets/svg/close-scan.svg";
import qr from "../../assets/svg/scan-qr.svg";
import blurImg from "../../assets/svg/blur-img.svg";
import { useLocation, useNavigate } from "react-router-dom";

export default function CheckingAsset() {
  const navigate = useNavigate();
  const location = useLocation();
  const [assetTag, setAssetTag] = useState("");

  const [isScannerActive, setIsScannerActive] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const isScanningRef = useRef(false);
  const [fieldError, setFieldError] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(null); // Tracks which button is loading: 'Validation', 'Checking', 'Daily entry'
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  useEffect(() => {
    const sessionMsg = sessionStorage.getItem("toastMessage");
    const sessionStatus = sessionStorage.getItem("toastStatus");
    if (sessionMsg) {
      setSnackbarMessage(sessionMsg);
      setSnackbarStatus(sessionStatus !== "false");
      handleSnackbarShow();
      sessionStorage.removeItem("toastMessage");
      sessionStorage.removeItem("toastStatus");
    } else if (location.state?.toastMessage) {
      setSnackbarMessage(location.state.toastMessage);
      setSnackbarStatus(location.state.toastStatus !== false);
      handleSnackbarShow();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const closeModal = () => {
    isScanningRef.current = false;
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (e) {}
      codeReaderRef.current = null;
    }
    setIsScannerActive(false);
  };

  useEffect(() => {
    if (!isScannerActive) return;
    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;
    isScanningRef.current = false;
    setErrorMessage(null);

    const timer = setTimeout(() => {
      if (videoRef.current) {
        codeReader
          .decodeFromVideoDevice(undefined, videoRef.current, (result, err) => {
            if (result && !isScanningRef.current) {
              isScanningRef.current = true;
              const text = result.getText ? result.getText() : (result.text || "");
              setAssetTag(text);
              setErrorMessage(null);
              closeModal();
            }
          })
          .catch((err) => {
            console.error("Camera access error:", err);
            setErrorMessage("Camera permission denied or camera not found.");
          });
      }
    }, 150);

    return () => {
      clearTimeout(timer);
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.reset();
        } catch (e) {}
        codeReaderRef.current = null;
      }
    };
  }, [isScannerActive]);
  const validateScanData = async (qrTag, type) => {
    setFieldError(null);
    setDisplayMessage(null);

    if (!qrTag || qrTag.trim() === "") {
      setFieldError("The asset tag field is required.");
      return;
    }

    setIsLoading(type); // Set loading for the clicked button

    try {
      const response = await getAssetInfoApi(qrTag);
      if (response.data.success === true) {
        navigate("/checking-view", { state: { assetDetail: response.data.asset, type: type } });

        if (response.data.data?.available_status === true) {
          closeModal();
        } else {
          setDisplayMessage("Dock not available");
        }
      } else {
        if (response.data.error && response.data.error.asset_tag) {
          setFieldError(response.data.error.asset_tag[0]);
        }
        setSnackbarMessage(response.data.message || "An error occurred");
        setSnackbarStatus(false);
        handleSnackbarShow();
      }
      return response.data;
    } catch (err) {
      if (err.response?.data?.error?.asset_tag) {
        setFieldError(err.response.data.error.asset_tag[0]);
      }
      setSnackbarMessage(err.response?.data?.message || "An unexpected error occurred");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsLoading(null); // Clear loading state
    }
  };

  return (
    <div className="container-fluid px-0" style={{ overflowY: "auto", width: "100%", height: "100vh", background: "#F0F4F9", overflowX: "hidden" }}>
      <Row style={{ background: "#FFFFFF" }}>
        <Col sm={12} md={6} lg={4}>
          <div className="my-5" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div className="qr-title">Scan QR code or Bar code</div>
            <div className="qr-sub-title">Place the QR code in the scanner area</div>
            <div className="my-3" style={{ width: "267px", textAlign: "center" }}>
              {isScannerActive === true ? (
                <div className="qr-image-wrapper">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: "267px",
                      height: "267px",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: "16px",
                    }}
                  />
                  <div className="qr-scanner-overlay"></div>
                  <div
                    className="style-justify-center cursor-pointer"
                    onClick={() => {
                      closeModal();
                    }}
                    style={{ position: "absolute", bottom: "14px", right: "14px", width: "77px", height: "32px", borderRadius: "100px", background: "#000000", opacity: "70%", zIndex: 10 }}
                  >
                    <img src={close} alt="Close" /> <span className="ps-1 font13-500-FFFFFF">Close</span>
                  </div>
                </div>
              ) : (
                <div className="qr-box-inactive" style={{ backgroundImage: `url(${blurImg})` }}>
                  <Button
                    className="scan-qr-btn"
                    onClick={() => {
                      isScanningRef.current = false;
                      setIsScannerActive(true);
                      setErrorMessage(null);
                      setDisplayMessage(null);
                    }}
                  >
                    <img src={qr} alt="QR" className="pe-2" /> Scan QR code
                  </Button>
                </div>
              )}
              <div>{displayMessage}</div>
              {errorMessage && <div style={{ color: "red" }}>{errorMessage}</div>}
            </div>
          </div>
        </Col>
        <Col sm={12} md={6} lg={8}>
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: "400px" }}>
              <div className="qr-title">Manual checking</div>
              <div className="qr-sub-title">Enter asset reference number or ID to check information</div>
              <div className="mt-3" style={{ textAlign: "left" }}>
                <input
                  type="text"
                  placeholder="Enter tag"
                  className={`input-field w-100 ${fieldError ? "is-invalid" : ""}`}
                  value={assetTag}
                  onChange={(e) => {
                    setAssetTag(e.target.value);
                    if (fieldError) setFieldError(null);
                    if (errorMessage) setErrorMessage(null);
                  }}
                  style={{ borderColor: fieldError ? "#dc3545" : undefined }}
                />
                {fieldError && <span style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px", display: "block" }}>{fieldError}</span>}
              </div>
              <div className="mt-3 style-justify-between">
                <Button
                  className="validation-btn"
                  onClick={() => {
                    validateScanData(assetTag, "Validation");
                  }}
                  disabled={isLoading !== null}
                >
                  {isLoading === "Validation" && <Spinner animation="border" size="sm" className="me-2" style={{ color: "inherit" }} />}
                  Validation
                </Button>
                <Button
                  className="checking-btn"
                  onClick={() => {
                    validateScanData(assetTag, "Checking");
                  }}
                  disabled={isLoading !== null}
                >
                  {isLoading === "Checking" && <Spinner animation="border" size="sm" className="me-2" style={{ color: "inherit" }} />}
                  Checking
                </Button>
                <Button
                  className="daily-entry-btn"
                  onClick={() => {
                    validateScanData(assetTag, "Daily entry");
                  }}
                  disabled={isLoading !== null}
                >
                  {isLoading === "Daily entry" && <Spinner animation="border" size="sm" className="me-2" style={{ color: "inherit" }} />}
                  Daily entry
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Snackbar Toast */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={handleSnackbarClose} delay={3000} autohide>
          <Toast.Body className="text-white">{snackbarMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
